import { type FC, type ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
	Extrapolation,
	interpolate,
	useAnimatedReaction,
	useAnimatedStyle,
	useDerivedValue,
	type SharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useHeaderHeight } from '@react-navigation/elements';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Styles from 'src/core/utils/Styles';
import { HeaderFadeProvider } from 'src/presentation/hooks/useHeaderFade';

interface Props {
	scrollY: SharedValue<number>;
	maxHeight: number;
	title?: string | undefined;
	headerLeft?: ReactNode;
	headerRight?: ReactNode;
	onSolidChange?: (isSolid: boolean) => void;
}

const StickyHeader: FC<Props> = ({ scrollY, maxHeight, title, headerLeft, headerRight, onSolidChange }) => {
	const headerHeight = useHeaderHeight();
	const insets = useSafeAreaInsets();

	const fade = useDerivedValue(() =>
		interpolate(scrollY.value, [headerHeight, maxHeight], [Dimens.opacity0, Dimens.opacity100], {
			extrapolateRight: Extrapolation.CLAMP,
		}),
	);

	const animatedOpacityStyle = useAnimatedStyle(() => ({ opacity: fade.value }));

	useAnimatedReaction(
		() => fade.value > Dimens.ratio50,
		(isSolid, wasSolid) => {
			if (onSolidChange && isSolid !== wasSolid) {
				scheduleOnRN(onSolidChange, isSolid);
			}
		},
		[onSolidChange],
	);

	return (
		<View style={[styles.container, { height: headerHeight }]}>
			<Animated.View style={[styles.background, { paddingTop: insets.top }, animatedOpacityStyle]}>
				<Text style={styles.title}>{title}</Text>
			</Animated.View>
			<View style={[styles.bar, { paddingTop: insets.top }]}>
				<HeaderFadeProvider fade={fade}>
					<View style={styles.barSide}>{headerLeft}</View>
					<View style={styles.barSide}>{headerRight}</View>
				</HeaderFadeProvider>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		position: 'absolute',
		top: Dimens.padding0,
		left: Dimens.padding0,
		zIndex: Dimens.zIndexStickyHeader,
		width: '100%',
	},
	background: {
		position: 'absolute',
		top: Dimens.padding0,
		left: Dimens.padding0,
		height: '100%',
		width: '100%',
		backgroundColor: Colors.white,
		...Styles.header.shadow,
		flex: Dimens.flex1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	title: {
		...Styles.header.title,
		lineHeight: Dimens.lineHeight24,
		textAlign: 'center',
	},
	bar: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		flex: Dimens.flex1,
		paddingHorizontal: Dimens.padding18,
	},
	barSide: {
		flexDirection: 'row',
		gap: Dimens.gap10,
	},
});

export default StickyHeader;
